var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@ideagen/helix-react-core';
import { Button, Divider, HelixPalette, HelixStyles, IChevronLeft, ICross, IFileText, IWeb, Typography } from '@ideagen/helix-react-core';
import { useTranslation } from 'react-i18next';
import { HELIX_REACT_AI_TRANSLATION_NAMESPACE } from '../../locales';
import { isValidUrl, getPresignedUrl } from '../utils/helper';
import { useAgentConfig } from '../../contexts/AgentConfigContext';
/**
 * CitationsView component for displaying citations in a dedicated view
 */
export const CitationsView = ({ citations, dataId, onBack, sidePanel = false, authToken }) => {
    /**
     * Determines if a citation source is a document or link based on its filename or URI
     */
    const getCitationType = (filename, uri) => {
        const documentExtensions = ['.pdf', '.doc', '.docx', '.txt', '.xls', '.xlsx', '.ppt', '.pptx', '.csv', '.rtf'];
        const lowerFilename = filename.toLowerCase();
        const lowerUri = uri.toLowerCase();
        // Check if filename has a document extension
        if (documentExtensions.some((ext) => lowerFilename.includes(ext) || lowerUri.includes(ext))) {
            return 'document';
        }
        return 'link';
    };
    /**
     * Returns the appropriate icon/indicator based on citation type
     */
    const getCitationIndicator = (filename, uri, size = 12) => {
        const type = getCitationType(filename, uri);
        return type === 'document' ? _jsx(IFileText, { size: size }) : _jsx(IWeb, { size: size });
    };
    const { config, getAgentBaseUrlFromAgentUrl } = useAgentConfig();
    // Handles click on S3 URI
    const handleCitationClick = (uri) => __awaiter(void 0, void 0, void 0, function* () {
        const baseUrl = (config === null || config === void 0 ? void 0 : config.agent_url) ? getAgentBaseUrlFromAgentUrl(config.agent_url) : undefined;
        if (!baseUrl) {
            console.error('Base URL not provided for presigned URL generation');
            return;
        }
        const presignedUrl = yield getPresignedUrl(uri, baseUrl, authToken);
        if (presignedUrl && isValidUrl(presignedUrl)) {
            // Create a temporary anchor element for better cross-framework compatibility
            const link = document.createElement('a');
            link.href = presignedUrl;
            link.target = '_blank';
            link.rel = 'noopener,noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        else {
            console.error('Invalid URL:', presignedUrl);
        }
    });
    const { t } = useTranslation();
    return (_jsxs(Box, { width: "100%", height: "100%", maxWidth: "720px", flexDirection: "column", display: "flex", paddingTop: "8px", children: [_jsxs(Box, { display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 2, children: [_jsx(Typography, Object.assign({}, HelixStyles.style_02, { color: HelixPalette.neutral100, children: "Sources" })), sidePanel ? (_jsx(Button, { dataId: `${dataId}-close-button`, size: "large", variant: "grey", type: "universal", leftIcon: _jsx(ICross, {}), onClick: onBack, title: t('commands.close', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }) })) : (_jsx(Button, { dataId: `${dataId}-back-button`, size: "large", variant: "teal", type: "universal", leftIcon: _jsx(IChevronLeft, {}), onClick: onBack, label: t('commands.back', { ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE }) }))] }), _jsx(Divider, {}), _jsx(Box, { sx: { flex: 1, overflowY: 'auto' }, children: citations && citations.length > 0 ? (_jsx(Box, { children: citations.map((citation, index) => {
                        var _a;
                        const iconSize = citation.title ? 12 : 14;
                        const isS3Uri = (_a = citation.uri) === null || _a === void 0 ? void 0 : _a.startsWith('s3://');
                        const isClickable = !!citation.uri;
                        return (_jsxs(Box, Object.assign({ component: isS3Uri || !citation.uri ? 'div' : 'a' }, (!isS3Uri && citation.uri ? {
                            href: citation.uri,
                            target: "_blank",
                            rel: "noopener noreferrer"
                        } : {}), { marginBottom: "0.75rem", padding: "0.5rem", borderRadius: "8px", sx: {
                                backgroundColor: HelixPalette.white,
                                textDecoration: 'none',
                                cursor: isClickable ? 'pointer' : 'default',
                                display: 'block',
                                '&:hover': isClickable
                                    ? {
                                        textDecoration: 'none',
                                        backgroundColor: HelixPalette.teal05,
                                    }
                                    : {},
                                '&:active': isClickable
                                    ? {
                                        textDecoration: 'none',
                                        backgroundColor: HelixPalette.teal10,
                                    }
                                    : {},
                            }, onClick: () => {
                                if (isS3Uri) {
                                    handleCitationClick(citation.uri);
                                }
                            }, children: [_jsxs(Box, { display: "flex", flexDirection: "row", alignItems: "center", gap: "2px", marginBottom: "2px", children: [getCitationIndicator(citation.filename, citation.uri, iconSize), _jsx(Typography, Object.assign({}, (citation.title ? HelixStyles.style_12 : HelixStyles.style_04), HelixStyles.truncate, { color: citation.title ? HelixPalette.neutral90 : HelixPalette.neutral100, children: citation.filename }))] }), _jsx(Typography, Object.assign({}, HelixStyles.style_04, { color: HelixPalette.neutral100, marginBottom: "4px", sx: {
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                    }, children: citation.title })), _jsx(Typography, Object.assign({}, HelixStyles.style_12, { color: HelixPalette.neutral70, sx: {
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                    }, children: citation.publisher }))] }), `${dataId}-citation-${index}`));
                    }) })) : (_jsx(Typography, Object.assign({}, HelixStyles.style_12, { color: HelixPalette.neutral90, children: "No citations available for this message." }))) })] }));
};
export default CitationsView;
